<template>
  <portal to="modal-windows">
    <AppDialog
      :disable-confirm-button="!isNumber(selectedModuleId)"
      :loading-confirm-button="loading"
      :show="show"
      :size="792"
      :title="$t('dashboard.dashboard_modules')"
      :type="DIALOG_TYPES.INFO"
      hide-footer
      hide-hero
      style="
        --dialog-content-padding-right: 28px;
        --dialog-content-padding-left: 28px;
        --footer-padding-top: 32px;
        --footer-padding-bottom: 32px;
        --footer-padding-right: 28px;
      "
      @on-close="hideCreateModuleModal"
      @on-confirm="onConfirmCreate"
    >
      <div>
        <span class="scd-ModuleSubtitle">{{ $t('dashboard.dashboard_modules_description') }}</span>

        <div class="scd-ModuleList">
          <div
            v-for="module in modules"
            :key="module.id"
            :class="{
              'scd-ModuleListItem-selected': isModuleSelected(module.id),
              'scd-ModuleListItem-disabled': module.status === MODULE_STATUSES.COMING
            }"
            :style="{ '--module-color': module.color }"
            class="scd-ModuleListItem"
            @click="selectModule(module)"
          >
            <div class="scd-ModuleContent">
              <AppIcon :icon-name="module.icon" class="scd-ModuleIcon" height="40px" width="40px" />
              <div>
                <span class="scd-ModuleListItem_Title">{{ module.title }}</span>
                <span class="scd-ModuleListItem_Description">
                  {{ module.description }}
                </span>
              </div>
            </div>

            <AppBadge
              v-if="module.status === MODULE_STATUSES.COMING"
              :content="$t('common.coming_soon')"
              class="scd-Badge"
              inline
              is-uppercase
            />
          </div>
        </div>
      </div>
    </AppDialog>
  </portal>
</template>

<script setup>
import { isNumber } from 'lodash'
import { Portal } from 'portal-vue'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
import { tracker } from '@/tracking/amplitude'
import { EVENT_NAMES } from '@/tracking/gtm-helpers'
import { gtmTracker } from '@/tracking/gtm-tracking'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import {
  CUSTOM_DASHBOARD_MODULE_ATTRIBUTES,
  CUSTOM_DASHBOARD_MODULE_TYPES_FOR_TRACKING,
  MODULE_TYPE_IDS
} from '@/utils/custom-dashboard-helper'
import { handleError } from '@/utils/error-handling'

import AppDialog from '@/components/AppDialog'
import AppBadge from '@/components/ui/AppBadge/AppBadge'
import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'ChooseCustomDashboardModulesDialog'
})
const emit = defineEmits({
  'update:show': null,
  created: null
})

const props = defineProps({
  show: {
    type: Boolean
  },

  item: {
    type: Object,
    default: () => ({})
  }
})

const MODULE_STATUSES = {
  AVAILABLE: 'available',
  COMING: 'coming'
}

const { t } = useI18n()
const loading = ref(false)

const modules = [
  {
    id: MODULE_TYPE_IDS.OKR,
    title: t('dashboard.okr_module'),
    description: t('dashboard.okr_module_subtitle'),
    titleToCreate: 'Untitled OKR Module',
    status: MODULE_STATUSES.AVAILABLE,
    icon: 'okr-module',
    color: 'var(--grade-medium-color-next)',
    gtmEventName: EVENT_NAMES.OKR_MODULE_ADDED,
    amplitudeModuleType: CUSTOM_DASHBOARD_MODULE_TYPES_FOR_TRACKING[MODULE_TYPE_IDS.OKR],
    defaultParams: {
      layoutTypeId: CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.LAYOUT_TYPE_ID.defaultValue
    }
  },
  {
    id: MODULE_TYPE_IDS.KPI,
    title: t('dashboard.kpi_module'),
    description: t('dashboard.kpi_module_subtitle'),
    titleToCreate: 'Untitled KPI Module',
    status: MODULE_STATUSES.AVAILABLE,
    icon: 'kpi-module',
    color: 'var(--grade-low-color-next)',
    gtmEventName: EVENT_NAMES.KPI_MODULE_ADDED,
    amplitudeModuleType: CUSTOM_DASHBOARD_MODULE_TYPES_FOR_TRACKING[MODULE_TYPE_IDS.KPI],
    defaultParams: {
      activeColumns: CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.ACTIVE_COLUMNS.defaultValue
    }
  },
  {
    id: MODULE_TYPE_IDS.TEXT,
    title: t('dashboard.text_module'),
    description: t('dashboard.text_module_subtitle'),
    titleToCreate: 'Text Module',
    status: MODULE_STATUSES.AVAILABLE,
    icon: 'text-module',
    color: 'var(--dark-2)',
    gtmEventName: EVENT_NAMES.TEXT_MODULE_ADDED,
    amplitudeModuleType: CUSTOM_DASHBOARD_MODULE_TYPES_FOR_TRACKING[MODULE_TYPE_IDS.TEXT],
    defaultParams: {
      textValue: ''
    }
  }
  // {
  //   id: MODULE_TYPE_IDS.CHECK_IN,
  //   title: t('dashboard.checkin_module'),
  //   description: t('dashboard.checkin_module_subtitle'),
  //   status: MODULE_STATUSES.COMING,
  //   icon: 'checkin-module',
  //   color: 'var(--grade-high-color-next)'
  // }
]

const isModuleSelected = id => selectedModuleId.value === id

const hideCreateModuleModal = () => {
  emit('update:show', false)
  selectedModuleId.value = null
}

const selectedModuleId = ref(null)
const onConfirmCreate = async () => {
  const selectedModule = modules.find(module => module.id === selectedModuleId.value)

  if (!selectedModule) return
  try {
    loading.value = true

    const api = new CustomDashboardApiHandler()
    const defaultParams = selectedModule.defaultParams || {}

    await api.createModule({
      moduleTypeId: selectedModuleId.value,
      id: props.item.id,
      name: selectedModule.titleToCreate,
      ...defaultParams
    })

    emit('created', props.item)
    gtmTracker.logEvent(selectedModule.gtmEventName)
    tracker.logEvent('dashboard module added', {
      moduleType: selectedModule.amplitudeModuleType,
      id: props.item.id
    })

    hideCreateModuleModal()
  } catch (error) {
    handleError({ error })
  } finally {
    loading.value = false
  }
}

const selectModule = ({ status, id }) => {
  if (status === MODULE_STATUSES.AVAILABLE) {
    selectedModuleId.value = id
    onConfirmCreate()
  }
}
</script>

<style lang="scss" scoped>
.scd-ModuleList {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 0 52px 0;
}

.scd-ModuleListItem {
  padding: 14px;
  border: 1px solid $grey-2-next;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 24px;
  cursor: pointer;
  justify-content: space-between;
  &-selected {
    border-color: var(--module-color);
  }
  &-disabled {
    cursor: not-allowed;
  }
}

.scd-ModuleIcon {
  padding: 20px;
  border-radius: 16px;
  height: 80px;
  width: 80px;
  background: var(--module-color);
}

.scd-ModuleListItem_Title {
  font-size: $fs-18;
  line-height: 24px;
  font-weight: fw('bold');
  color: $dark-1;
  display: flex;
  margin: 0 0 8px 0;

  .scd-ModuleListItem-selected & {
    color: $primary-color-next;
  }
}

.scd-ModuleListItem_Description {
  font-size: $fs-16;
  line-height: 24px;
  color: $dark-1;
}

.scd-ModuleSubtitle {
  font-size: $fs-16;
  line-height: 24px;
  margin: 0 0 48px 0;
  color: $dark-1;
  display: flex;
}

.scd-Badge {
  --color: var(--grade-high-color-next);
  --font-size: 16px;
  --badge-size: 40px;
  --padding: 15px 13px;
}

.scd-ModuleContent {
  display: flex;
  align-items: center;
  gap: 24px;
}
</style>
